import type RAPIER from '@dimforge/rapier3d-compat';
import {
  addV3,
  clamp,
  copyQuat,
  copyV3,
  crossV3,
  dotV3,
  FORWARD,
  lerp,
  lerpV3,
  lengthV3,
  normalizeQuat,
  normalizeV3,
  quatFromAxisAngle,
  quatIdentity,
  quatMul,
  quatRotateVec3,
  RIGHT,
  scaleV3,
  setV3,
  subV3,
  UP,
  v3,
  type Quat,
  type Vec3,
} from './math.js';
import { DEFAULT_SURFACE, SURFACES, surfaceIdToName, type SurfaceName } from './surfaces.js';
import { EMPTY_INPUT, type VehicleInput } from './input.js';

export interface VehicleConfig {
  halfExtents: Vec3;
  /** Hover height above contact (suspension rest length). */
  hoverHeight: number;
  /** Max distance a ray probes for ground. */
  rayLength: number;
  /** Forward accel m/s². */
  accelForward: number;
  /** Reverse accel m/s². */
  accelReverse: number;
  /** Brake decel m/s². */
  brakeDecel: number;
  /** Top speed m/s (~25 m/s = ~90 km/h). */
  maxSpeed: number;
  /** Linear drag coeff. */
  baseDrag: number;
  /** Air drag coeff (no surface contact). */
  airDrag: number;
  /** Yaw rate rad/s at low speed. */
  steerRate: number;
  /** Multiplier applied to lateral velocity when grounded (1 = no slide). */
  baseLateralGrip: number;
  /** Multiplier on steerRate while drifting. */
  driftSteerMul: number;
  /** Multiplier on grip while drifting (lower = slidier). */
  driftGripMul: number;
  /** Boost top-speed multiplier. */
  boostSpeedMul: number;
  /** Boost accel m/s². */
  boostAccel: number;
  /** Time after last contact before switching to world gravity. */
  airborneGraceMs: number;
  /** World gravity used only when airborne. */
  airGravity: number;
  /** Magnitude of pull-to-ground impulse while grounded (m/s² along -groundNormal). */
  surfacePull: number;
  /** Max angular rate around car-local X axis when airborne (rad/s). */
  airPitchRate: number;
  /** Max angular rate around car-local Z (forward) axis when airborne (rad/s). */
  airRollRate: number;
  /** ms of low-speed airborne before we self-right to upright (rescue stuck-inverted). */
  selfRightAfterMs: number;
}

export const DEFAULT_VEHICLE_CONFIG: VehicleConfig = {
  halfExtents: v3(0.6, 0.35, 1.2),
  hoverHeight: 0.45,
  rayLength: 1.8,
  accelForward: 22,
  accelReverse: 10,
  brakeDecel: 38,
  maxSpeed: 32,
  baseDrag: 0.4,
  airDrag: 0.06,
  steerRate: 2.6,
  baseLateralGrip: 8.0,
  driftSteerMul: 1.45,
  driftGripMul: 0.18,
  boostSpeedMul: 1.4,
  boostAccel: 50,
  airborneGraceMs: 120,
  airGravity: 28,
  surfacePull: 36,
  airPitchRate: 4.2,
  airRollRate: 3.6,
  selfRightAfterMs: 2500,
};

export interface VehicleSnapshot {
  id: number;
  position: Vec3;
  quaternion: Quat;
  linvel: Vec3;
  groundNormal: Vec3;
  isGrounded: boolean;
  surface: SurfaceName;
  speedMs: number;
  driftPower: number;
  airTimeMs: number;
}

const tmpRayHitNormal = v3();
const tmpAvgNormal = v3();
const tmpFwd = v3();
const tmpRight = v3();
const tmpUp = v3();
const tmpLatVel = v3();
const tmpFlatFwd = v3();
const tmpAccel = v3();
const tmpDelta = v3();
const tmpDown = v3();
const tmpCornerLocal = v3();
const tmpCornerWorld = v3();
const tmpFromTo = v3();
const yawQuat: Quat = { x: 0, y: 0, z: 0, w: 1 };
const tiltQuat: Quat = { x: 0, y: 0, z: 0, w: 1 };
const tmpQuat: Quat = { x: 0, y: 0, z: 0, w: 1 };

export class Vehicle {
  readonly id: number;
  readonly config: VehicleConfig;

  position: Vec3 = v3();
  quaternion: Quat = quatIdentity();
  linvel: Vec3 = v3();

  groundNormal: Vec3 = v3(0, 1, 0);
  isGrounded = false;
  airTimeMs = 0;
  surface: SurfaceName = DEFAULT_SURFACE;
  driftPower = 0;

  /** Cached Rapier Cuboid shape used for collision sweep. Lazy-init in step(). */
  private chassisShape: unknown = null;
  /** Bounce coefficient on static wall hits (0 = inelastic, 1 = elastic). */
  private wallBounce = 0.08;
  /** Bounce coefficient on car-vs-car hits — feels best a bit higher than wall. */
  private carBounce = 0.55;

  /** Optional kinematic body in the Rapier world used so OTHER cars can find us in shape-casts. */
  kinematicBody: RAPIER.RigidBody | null = null;

  /** Most recent collision event with another vehicle — read + cleared by client code each frame. */
  lastCarHit: {
    otherId: number;
    speedDelta: number; // |Δv| applied (m/s)
  } | null = null;

  constructor(id: number, config: Partial<VehicleConfig> = {}) {
    this.id = id;
    this.config = { ...DEFAULT_VEHICLE_CONFIG, ...config };
  }

  /** Called by SimWorld.addVehicle once the kinematic body has been created. */
  attachKinematicBody(rb: RAPIER.RigidBody): void {
    this.kinematicBody = rb;
    this.syncKinematicBody();
  }

  /** Push current pose into the Rapier kinematic body so shape-casts see us correctly. */
  syncKinematicBody(): void {
    const rb = this.kinematicBody;
    if (!rb) return;
    rb.setNextKinematicTranslation(this.position);
    rb.setNextKinematicRotation(this.quaternion);
  }

  /** External impulse — applied directly to linear velocity. */
  applyImpulse(impulse: Vec3): void {
    this.linvel.x += impulse.x;
    this.linvel.y += impulse.y;
    this.linvel.z += impulse.z;
  }

  setTransform(position: Vec3, quaternion: Quat = quatIdentity()): void {
    copyV3(this.position, position);
    copyQuat(this.quaternion, quaternion);
    setV3(this.linvel, 0, 0, 0);
    setV3(this.groundNormal, 0, 1, 0);
    this.isGrounded = false;
    this.airTimeMs = 0;
    this.driftPower = 0;
  }

  /** Reset to a spawn pose (used for /reset input and respawn). */
  respawn(spawnPos: Vec3, spawnQuat: Quat): void {
    this.setTransform(spawnPos, spawnQuat);
  }

  /**
   * Advance one fixed-dt step. Reads `input`, queries world via raycasts,
   * integrates manually. World is the Rapier world for queries only — we do
   * NOT add this vehicle as a dynamic body to Rapier.
   *
   * @param vehicles If provided, car-vs-car shape-cast hits will look up the
   *                  other Vehicle via collider userData.vehicleId and exchange
   *                  velocity along the contact normal (an elastic-ish bump).
   */
  step(
    rapier: typeof RAPIER,
    world: RAPIER.World,
    input: Readonly<VehicleInput> = EMPTY_INPUT,
    dt: number,
    vehicles?: ReadonlyMap<number, Vehicle>,
  ): void {
    const cfg = this.config;

    // --- 1. Sample ground via 4 corner raycasts along car-local down ---
    quatRotateVec3(tmpUp, this.quaternion, UP as Vec3);
    quatRotateVec3(tmpFwd, this.quaternion, FORWARD as Vec3);
    quatRotateVec3(tmpRight, this.quaternion, RIGHT as Vec3);
    scaleV3(tmpDown, tmpUp, -1);

    let hits = 0;
    let bestDist = Infinity;
    let bestSurface: SurfaceName = DEFAULT_SURFACE;
    setV3(tmpAvgNormal, 0, 0, 0);

    const corners = [
      [1, 1], [1, -1], [-1, 1], [-1, -1],
    ] as const;

    for (const [sx, sz] of corners) {
      // Local corner offset in chassis space.
      setV3(
        tmpCornerLocal,
        cfg.halfExtents.x * sx,
        -cfg.halfExtents.y, // bottom of chassis
        cfg.halfExtents.z * sz,
      );
      quatRotateVec3(tmpCornerWorld, this.quaternion, tmpCornerLocal);
      addV3(tmpCornerWorld, tmpCornerWorld, this.position);

      const ray = new rapier.Ray(tmpCornerWorld, tmpDown);
      const hit = world.castRay(ray, cfg.rayLength, true);
      if (hit && hit.timeOfImpact >= 0) {
        const normal = world.castRayAndGetNormal(ray, cfg.rayLength, true);
        if (normal) {
          tmpRayHitNormal.x = normal.normal.x;
          tmpRayHitNormal.y = normal.normal.y;
          tmpRayHitNormal.z = normal.normal.z;
          addV3(tmpAvgNormal, tmpAvgNormal, tmpRayHitNormal);
          hits++;
          if (hit.timeOfImpact < bestDist) {
            bestDist = hit.timeOfImpact;
            const collider = hit.collider;
            const ud = collider?.parent()?.userData as { surface?: SurfaceName } | undefined
              ?? (collider as unknown as { userData?: { surface?: SurfaceName } } | null)?.userData;
            if (ud && ud.surface && (SURFACES as Record<string, unknown>)[ud.surface] !== undefined) {
              bestSurface = ud.surface;
            }
          }
        }
      }
    }

    if (hits > 0) {
      scaleV3(tmpAvgNormal, tmpAvgNormal, 1 / hits);
      normalizeV3(tmpAvgNormal, tmpAvgNormal);
      copyV3(this.groundNormal, tmpAvgNormal);
      this.surface = bestSurface;

      // Suspension: snap to hoverHeight along normal if too close/far (cheap, no spring).
      const targetGap = cfg.hoverHeight;
      const gapError = bestDist - targetGap;
      // Move position along -down by -gapError so gap converges to hoverHeight.
      scaleV3(tmpDelta, tmpDown, gapError);
      addV3(this.position, this.position, tmpDelta);

      this.isGrounded = true;
      this.airTimeMs = 0;
    } else {
      this.isGrounded = false;
      this.airTimeMs += dt * 1000;
      // Note: we no longer force groundNormal toward world-up here. While
      // airborne we DON'T tilt the chassis (player can flip), and on landing
      // the next tick will pick up the real ground normal.
      this.surface = DEFAULT_SURFACE;
    }

    const surfaceProps = SURFACES[this.surface];

    // Threshold for "stunt-controllable airborne." Below this, the car is mid-
    // takeoff/landing transition and still uses ground-aligned steering.
    const stuntAir = !this.isGrounded && this.airTimeMs > cfg.airborneGraceMs;

    // --- 2. Build chassis local axes aligned to ground normal + current yaw ---
    // Yaw is the rotation of car forward around groundNormal.
    const fwdFlat = projectOntoPlane(tmpFlatFwd, tmpFwd, this.groundNormal);
    if (lengthV3(fwdFlat) < 1e-4) {
      // Forward is parallel to ground normal (rare gimbal). Use right axis.
      const rightFlat = projectOntoPlane(tmpFlatFwd, tmpRight, this.groundNormal);
      normalizeV3(tmpFlatFwd, rightFlat);
    } else {
      normalizeV3(tmpFlatFwd, fwdFlat);
    }

    // Local right = normal × forward
    crossV3(tmpRight, this.groundNormal, tmpFlatFwd);
    normalizeV3(tmpRight, tmpRight);

    const speed = lengthV3(this.linvel);
    const driftActive = input.drift && this.isGrounded && speed > 6;

    if (stuntAir) {
      // --- AIRBORNE: stunt controls ---
      //   throttle  → pitch around car-local X axis (W = front flip, S = back flip)
      //   steer     → roll  around car-local Z axis (A = roll left, D = roll right)
      // No yaw, no tilt-to-ground. Lets the car rotate freely.
      const pitchRate = -input.throttle * cfg.airPitchRate;
      const rollRate = -input.steer * cfg.airRollRate;

      if (Math.abs(pitchRate) > 1e-3) {
        // Car-local +X (right) in world space.
        quatRotateVec3(tmpRight, this.quaternion, RIGHT as Vec3);
        quatFromAxisAngle(yawQuat, tmpRight, pitchRate * dt);
        quatMul(this.quaternion, yawQuat, this.quaternion);
        normalizeQuat(this.quaternion, this.quaternion);
      }
      if (Math.abs(rollRate) > 1e-3) {
        // Roll is rotation around car-local forward (-Z in chassis space).
        quatRotateVec3(tmpFwd, this.quaternion, FORWARD as Vec3);
        quatFromAxisAngle(yawQuat, tmpFwd, rollRate * dt);
        quatMul(this.quaternion, yawQuat, this.quaternion);
        normalizeQuat(this.quaternion, this.quaternion);
      }

      // Re-derive flat-forward/right for the rest of the step (used by accel
      // section below, even though we won't apply throttle to motion in air).
      quatRotateVec3(tmpFwd, this.quaternion, FORWARD as Vec3);
      projectOntoPlane(tmpFlatFwd, tmpFwd, UP as Vec3);
      if (lengthV3(tmpFlatFwd) < 1e-4) setV3(tmpFlatFwd, 0, 0, -1);
      normalizeV3(tmpFlatFwd, tmpFlatFwd);
      crossV3(tmpRight, UP as Vec3, tmpFlatFwd);
      normalizeV3(tmpRight, tmpRight);
    } else {
      // --- GROUNDED (or grace window): yaw + tilt as before ---
      const steerRate =
        cfg.steerRate *
        (driftActive ? cfg.driftSteerMul : 1) *
        (speed < 0.3 ? 0 : 1);
      const yawDelta = -input.steer * steerRate * dt;
      quatFromAxisAngle(yawQuat, this.groundNormal, yawDelta);
      quatMul(this.quaternion, yawQuat, this.quaternion);
      normalizeQuat(this.quaternion, this.quaternion);

      // Re-derive forward after yaw.
      quatRotateVec3(tmpFwd, this.quaternion, FORWARD as Vec3);
      projectOntoPlane(tmpFlatFwd, tmpFwd, this.groundNormal);
      normalizeV3(tmpFlatFwd, tmpFlatFwd);
      crossV3(tmpRight, this.groundNormal, tmpFlatFwd);
      normalizeV3(tmpRight, tmpRight);

      // Tilt chassis to match ground normal (also self-rights after landing).
      quatRotateVec3(tmpUp, this.quaternion, UP as Vec3);
      quatFromToSafe(tiltQuat, tmpUp, this.groundNormal);
      quatMul(tmpQuat, tiltQuat, this.quaternion);
      slerpTowards(this.quaternion, this.quaternion, tmpQuat, clamp(dt * 18, 0, 1));
      normalizeQuat(this.quaternion, this.quaternion);
    }

    // --- 5. Accel along chassis forward (grounded-only) ---
    // In stuntAir we don't apply throttle to motion — input goes into rotation.
    setV3(tmpAccel, 0, 0, 0);
    if (!stuntAir) {
      if (input.brake) {
        const speedSign = dotV3(this.linvel, tmpFlatFwd);
        const brakeMag = cfg.brakeDecel;
        scaleV3(tmpDelta, tmpFlatFwd, -Math.sign(speedSign) * brakeMag);
        addV3(tmpAccel, tmpAccel, tmpDelta);
      } else if (input.throttle > 0) {
        scaleV3(tmpDelta, tmpFlatFwd, cfg.accelForward * input.throttle);
        addV3(tmpAccel, tmpAccel, tmpDelta);
      } else if (input.throttle < 0) {
        scaleV3(tmpDelta, tmpFlatFwd, cfg.accelReverse * input.throttle);
        addV3(tmpAccel, tmpAccel, tmpDelta);
      } else {
        const speedSign = dotV3(this.linvel, tmpFlatFwd);
        if (Math.abs(speedSign) > 0.05) {
          scaleV3(tmpDelta, tmpFlatFwd, -Math.sign(speedSign) * surfaceProps.rollResist);
          addV3(tmpAccel, tmpAccel, tmpDelta);
        }
      }

      // Boost
      if (input.boost) {
        scaleV3(tmpDelta, tmpFlatFwd, cfg.boostAccel);
        addV3(tmpAccel, tmpAccel, tmpDelta);
      }
      // Surface boost (pad)
      if (this.isGrounded && surfaceProps.boostAccel > 0) {
        scaleV3(tmpDelta, tmpFlatFwd, surfaceProps.boostAccel);
        addV3(tmpAccel, tmpAccel, tmpDelta);
      }
    }

    // --- 6. Integrate linvel ---
    scaleV3(tmpDelta, tmpAccel, dt);
    addV3(this.linvel, this.linvel, tmpDelta);

    // --- 7. Apply drag ---
    const dragCoef = this.isGrounded ? cfg.baseDrag + surfaceProps.drag : cfg.airDrag;
    const dragMul = Math.exp(-dragCoef * dt);
    scaleV3(this.linvel, this.linvel, dragMul);

    // --- 8. Lateral grip (if grounded — skipped when airborne) ---
    if (this.isGrounded && !stuntAir) {
      const latVelMag = dotV3(this.linvel, tmpRight);
      scaleV3(tmpLatVel, tmpRight, latVelMag);
      const gripCoef =
        surfaceProps.grip *
        cfg.baseLateralGrip *
        (driftActive ? cfg.driftGripMul : 1);
      const gripFactor = 1 - Math.exp(-gripCoef * dt);
      scaleV3(tmpDelta, tmpLatVel, -gripFactor);
      addV3(this.linvel, this.linvel, tmpDelta);
    }

    // --- 9. Gravity ---
    if (this.isGrounded) {
      // Pull onto ground along -normal to keep contact through bumps.
      scaleV3(tmpDelta, this.groundNormal, -cfg.surfacePull * dt);
      addV3(this.linvel, this.linvel, tmpDelta);
    } else {
      // Airborne: world gravity
      this.linvel.y -= cfg.airGravity * dt;
    }

    // --- 10. Clamp to max speed (boost-aware) ---
    const cap = cfg.maxSpeed * (input.boost ? cfg.boostSpeedMul : 1);
    const sp = lengthV3(this.linvel);
    if (sp > cap) {
      scaleV3(this.linvel, this.linvel, cap / sp);
    }

    // --- 11. Integrate position with shape-cast collision resolution ---
    // We sweep the chassis cuboid along linvel*dt and resolve wall hits
    // by sliding (project velocity onto wall plane). Floor-like hits
    // (normal.y > FLOOR_NORMAL_Y) are ignored — the down-rays already
    // handle ground following.
    const FLOOR_NORMAL_Y = 0.7;
    const SKIN = 0.02;
    scaleV3(tmpDelta, this.linvel, dt);
    const moveDist = lengthV3(tmpDelta);

    if (moveDist > 1e-5) {
      const invMove = 1 / moveDist;
      const dirX = tmpDelta.x * invMove;
      const dirY = tmpDelta.y * invMove;
      const dirZ = tmpDelta.z * invMove;

      // Lazy-init chassis shape (one allocation per vehicle).
      if (!this.chassisShape) {
        // Slight skin: shrink half-extents a hair to avoid sticking on coplanar surfaces.
        this.chassisShape = new rapier.Cuboid(
          cfg.halfExtents.x - SKIN,
          cfg.halfExtents.y - SKIN,
          cfg.halfExtents.z - SKIN,
        );
      }

      const hit = world.castShape(
        this.position,
        this.quaternion,
        { x: dirX, y: dirY, z: dirZ },
        this.chassisShape as RAPIER.Shape,
        0,
        moveDist + SKIN,
        true,
        undefined,
        undefined,
        undefined,
        // Exclude our own kinematic body so we don't self-hit.
        this.kinematicBody ?? undefined,
      );

      if (hit && hit.time_of_impact < moveDist + SKIN) {
        const n = hit.normal1; // Normal on the cast shape's side at contact.

        // Identify what we hit: another vehicle, or a static world collider?
        const ud = (hit.collider?.parent()?.userData ?? undefined) as
          | { kind?: 'vehicle' | 'world'; vehicleId?: number }
          | undefined;
        const isVehicle = ud?.kind === 'vehicle';
        const isFloor = !isVehicle && n.y > FLOOR_NORMAL_Y;

        // While airborne we treat floor-like hits as LANDING (block + zero out
        // downward velocity) instead of passing through. The next tick's ground
        // rays then re-attach the car to the surface.
        const wasAirborne = !this.isGrounded;

        if (isVehicle) {
          // Car-vs-car: bump. Move to contact, exchange normal-component velocity.
          const safeDist = Math.max(0, hit.time_of_impact - SKIN);
          this.position.x += dirX * safeDist;
          this.position.y += dirY * safeDist;
          this.position.z += dirZ * safeDist;

          const other =
            vehicles && ud?.vehicleId != null ? vehicles.get(ud.vehicleId) : null;
          if (other && other !== this) {
            // Relative velocity component along normal.
            const relVx = this.linvel.x - other.linvel.x;
            const relVy = this.linvel.y - other.linvel.y;
            const relVz = this.linvel.z - other.linvel.z;
            const relInto = relVx * n.x + relVy * n.y + relVz * n.z;
            if (relInto < 0) {
              // Equal-mass elastic-ish exchange of normal component.
              const j = -(1 + this.carBounce) * relInto * 0.5;
              this.linvel.x += n.x * j;
              this.linvel.y += n.y * j;
              this.linvel.z += n.z * j;
              other.linvel.x -= n.x * j;
              other.linvel.y -= n.y * j;
              other.linvel.z -= n.z * j;
              const speedDelta = Math.abs(j);
              this.lastCarHit = { otherId: other.id, speedDelta };
              other.lastCarHit = { otherId: this.id, speedDelta };
            }
          } else {
            // No other ref available — fall back to wall-style bounce.
            const intoSurface =
              this.linvel.x * n.x + this.linvel.y * n.y + this.linvel.z * n.z;
            if (intoSurface < 0) {
              const factor = -intoSurface * (1 + this.carBounce);
              this.linvel.x += n.x * factor;
              this.linvel.y += n.y * factor;
              this.linvel.z += n.z * factor;
            }
          }
        } else if (isFloor) {
          if (wasAirborne) {
            // LANDING: stop at contact, kill the inward (downward) component,
            // keep some horizontal motion so we slide naturally on touchdown.
            const safeDist = Math.max(0, hit.time_of_impact - SKIN);
            this.position.x += dirX * safeDist;
            this.position.y += dirY * safeDist;
            this.position.z += dirZ * safeDist;
            const intoSurface =
              this.linvel.x * n.x + this.linvel.y * n.y + this.linvel.z * n.z;
            if (intoSurface < 0) {
              // Kill the into-surface component plus a tiny bounce.
              const factor = -intoSurface * 1.05;
              this.linvel.x += n.x * factor;
              this.linvel.y += n.y * factor;
              this.linvel.z += n.z * factor;
            }
            // Damp horizontal slightly so landings feel "planted."
            this.linvel.x *= 0.9;
            this.linvel.z *= 0.9;
            // Force-attach to ground for the rest of this tick; ground rays
            // re-confirm next tick.
            this.isGrounded = true;
            this.airTimeMs = 0;
            this.groundNormal.x = n.x;
            this.groundNormal.y = n.y;
            this.groundNormal.z = n.z;
          } else {
            // Already grounded — floor contact while moving along the ground is
            // non-blocking; the suspension snap handles vertical offset.
            this.position.x += tmpDelta.x;
            this.position.y += tmpDelta.y;
            this.position.z += tmpDelta.z;
          }
        } else {
          // Wall hit: move to just-before contact, then slide.
          const safeDist = Math.max(0, hit.time_of_impact - SKIN);
          this.position.x += dirX * safeDist;
          this.position.y += dirY * safeDist;
          this.position.z += dirZ * safeDist;

          // Project linvel onto the contact plane (remove the inward component).
          // n is the normal on our cast shape, pointing FROM the obstacle. We want
          // to kill any velocity flowing INTO the obstacle.
          const intoSurface =
            this.linvel.x * n.x + this.linvel.y * n.y + this.linvel.z * n.z;
          // intoSurface > 0 means velocity is aligned with our "away from obstacle"
          // direction (i.e. we're already moving away — no penetration). We only
          // act when intoSurface < 0 (moving into the wall).
          if (intoSurface < 0) {
            const factor = -intoSurface * (1 + this.wallBounce);
            this.linvel.x += n.x * factor;
            this.linvel.y += n.y * factor;
            this.linvel.z += n.z * factor;
          }

          // Use the remaining motion budget to slide along the wall.
          const remaining = moveDist + SKIN - hit.time_of_impact;
          if (remaining > 1e-4) {
            // Slide direction = original direction projected onto contact plane.
            const dotND = dirX * n.x + dirY * n.y + dirZ * n.z;
            const slideX = dirX - n.x * dotND;
            const slideY = dirY - n.y * dotND;
            const slideZ = dirZ - n.z * dotND;
            const slideLen = Math.hypot(slideX, slideY, slideZ);
            if (slideLen > 1e-4) {
              const slideStep = remaining * 0.8; // light friction on slide
              const inv = slideStep / slideLen;
              this.position.x += slideX * inv;
              this.position.y += slideY * inv;
              this.position.z += slideZ * inv;
            }
          }
        }
      } else {
        // No hit — apply full motion.
        this.position.x += tmpDelta.x;
        this.position.y += tmpDelta.y;
        this.position.z += tmpDelta.z;
      }
    }

    // --- 12. Drift power meter (visual / boost-charge later) ---
    if (driftActive) {
      const lateral = Math.abs(dotV3(this.linvel, tmpRight));
      this.driftPower = clamp(this.driftPower + lateral * dt * 0.6, 0, 100);
    } else {
      this.driftPower = lerp(this.driftPower, 0, clamp(dt * 4, 0, 1));
    }
  }

  snapshot(out?: VehicleSnapshot): VehicleSnapshot {
    const s: VehicleSnapshot =
      out ??
      ({
        id: this.id,
        position: v3(),
        quaternion: { x: 0, y: 0, z: 0, w: 1 },
        linvel: v3(),
        groundNormal: v3(0, 1, 0),
        isGrounded: false,
        surface: DEFAULT_SURFACE,
        speedMs: 0,
        driftPower: 0,
        airTimeMs: 0,
      } as VehicleSnapshot);
    s.id = this.id;
    copyV3(s.position, this.position);
    copyQuat(s.quaternion, this.quaternion);
    copyV3(s.linvel, this.linvel);
    copyV3(s.groundNormal, this.groundNormal);
    s.isGrounded = this.isGrounded;
    s.surface = this.surface;
    s.speedMs = lengthV3(this.linvel);
    s.driftPower = this.driftPower;
    s.airTimeMs = this.airTimeMs;
    return s;
  }
}

/** Project vector v onto plane defined by unit normal n; result into out. */
function projectOntoPlane(out: Vec3, v: Vec3, n: Vec3): Vec3 {
  const d = dotV3(v, n);
  out.x = v.x - n.x * d;
  out.y = v.y - n.y * d;
  out.z = v.z - n.z * d;
  return out;
}

/** Safe slerp toward target (no allocations, in-place writes to `out`). */
function slerpTowards(out: Quat, a: Quat, b: Quat, t: number): void {
  let cos = a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
  let bx = b.x;
  let by = b.y;
  let bz = b.z;
  let bw = b.w;
  if (cos < 0) {
    cos = -cos;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let s0: number;
  let s1: number;
  if (cos > 0.9995) {
    s0 = 1 - t;
    s1 = t;
  } else {
    const omega = Math.acos(cos);
    const sinOmega = Math.sin(omega);
    s0 = Math.sin((1 - t) * omega) / sinOmega;
    s1 = Math.sin(t * omega) / sinOmega;
  }
  out.x = a.x * s0 + bx * s1;
  out.y = a.y * s0 + by * s1;
  out.z = a.z * s0 + bz * s1;
  out.w = a.w * s0 + bw * s1;
}

function quatFromToSafe(out: Quat, from: Vec3, to: Vec3): Quat {
  const d = dotV3(from, to);
  if (d > 0.999999) {
    out.x = 0;
    out.y = 0;
    out.z = 0;
    out.w = 1;
    return out;
  }
  if (d < -0.999999) {
    const axis = Math.abs(from.x) < 0.9 ? v3(1, 0, 0) : v3(0, 1, 0);
    crossV3(tmpFromTo, from, axis);
    normalizeV3(tmpFromTo, tmpFromTo);
    out.x = tmpFromTo.x;
    out.y = tmpFromTo.y;
    out.z = tmpFromTo.z;
    out.w = 0;
    return out;
  }
  crossV3(tmpFromTo, from, to);
  out.x = tmpFromTo.x;
  out.y = tmpFromTo.y;
  out.z = tmpFromTo.z;
  out.w = 1 + d;
  return normalizeQuat(out, out);
}
